import { hasModulePermission, isSuperAdmin } from '@/utils/permission'

const ROUTE_BY_CODE = {
  // 数据看板
  'dashboard:overall': '/dashboard',

  // 设备
  device: '/device',
  'device:menu': '/device',
  'device:page': '/device',
  'device-inspections': '/qc-record',
  'device-inspections:page': '/qc-record',

  // 用户 / 订单
  user: '/user',
  'user-menu': '/user',
  'user:page': '/user',
  'invite-partner': '/invite-partner',
  'invite-partner:menu': '/invite-partner',
  'invite-partner:page': '/invite-partner',
  partners: '/invite-partner',
  'partners:manage': '/invite-partner',
  'partners:page': '/invite-partner',
  order: '/order',
  'order:menu': '/order',
  'order:page': '/order',
  'order:biz-operator': '/operator-order',
  'order:biz-operator:page': '/operator-order',

  // 活动 / 套餐（运营配置页）
  package: '/operation',
  'pricing-rules': '/operation',
  'pricing-rules:list': '/operation',

  // 运营商：仅菜单码映射到页面；API 码 biz-operator:page 一二线共用，不映射路径
  'biz-operator:one': '/operator',
  'biz-operator:second': '/operator-level2',
  'biz-operator:bill': '/operator-bill',
  bill: '/operator-bill',
  'bill:page': '/operator-bill',

  // 站点 / 运维
  site: '/site',
  'site-menu': '/site',
  'site:page': '/site',
  'ops-devices': '/maintenance',
  'ops-devices:menu': '/maintenance',
  'ops-devices:page': '/maintenance',

  // 系统管理
  'admin-users': '/account',
  'admin-users:page': '/account',
  role: '/role',
  'role:list': '/role',
  company: '/enterprise',
  'company:page': '/enterprise',
  'sys-opr-log': '/oper-log',
  'oper-logs': '/oper-log',
  'oper-logs:page': '/oper-log'
}

const ROUTE_BY_NAME = {
  整体看板: '/dashboard',
  设备管理: '/device',
  质检记录: '/qc-record',
  用户管理: '/user',
  邀请合伙人: '/invite-partner',
  合伙人管理: '/invite-partner',
  订单管理: '/order',
  运营商订单管理: '/operator-order',
  角色管理: '/role',
  套餐管理: '/operation',
  运营配置: '/operation',
  运维管理: '/maintenance',
  账户管理: '/account',
  企业管理: '/enterprise',
  运营商管理: '/operator',
  一级运营商列表: '/operator',
  二级运营商管理: '/operator-level2',
  二级运营商列表: '/operator-level2',
  运营商对账管理: '/operator-bill',
  站点管理: '/site',
  操作日志: '/oper-log'
}

const ICON_BY_PATH = {
  '/dashboard': 'el-icon-s-data',
  '/device': 'el-icon-s-tools',
  '/qc-record': 'el-icon-document-checked',
  '/user': 'el-icon-user',
  '/invite-partner': 'el-icon-share',
  '/order': 'el-icon-s-order',
  '/operator-order': 'el-icon-s-order',
  '/role': 'el-icon-s-finance',
  '/operation': 'el-icon-s-marketing',
  '/maintenance': 'el-icon-s-tools',
  '/account': 'el-icon-user-solid',
  '/enterprise': 'el-icon-office-building',
  '/operator': 'el-icon-s-custom',
  '/operator-level2': 'el-icon-s-custom',
  '/operator-bill': 'el-icon-s-finance',
  '/site': 'el-icon-location-outline',
  '/oper-log': 'el-icon-document'
}

function normalizeType(permission) {
  return String((permission && permission.permissionType) || '')
    .trim()
    .toLowerCase()
}

function isButtonPermission(permission) {
  const type = normalizeType(permission)
  return type === 'api' || type === 'button'
}

function resolvePath(permission) {
  if (!permission) return ''
  const code = String(permission.permissionCode || '').trim()
  return ROUTE_BY_CODE[code] || ROUTE_BY_NAME[permission.permissionName] || ''
}

function isMenuPermission(permission) {
  if (!permission) return false
  const type = normalizeType(permission)
  if (type === 'menu') return true
  if (isButtonPermission(permission)) return false
  return !!resolvePath(permission)
}

function toMenuItem(permission, path) {
  return {
    id: permission.id,
    title: permission.permissionName,
    path,
    icon: ICON_BY_PATH[path] || 'el-icon-menu',
    permissionCode: permission.permissionCode
  }
}

function dedupeByPath(items) {
  const seen = new Set()
  return items.filter((item) => {
    if (!item.path || seen.has(item.path)) return false
    seen.add(item.path)
    return true
  })
}

// 按 getOauthInfo.permissions 生成侧边栏：
// 一级 MENU = 分组，二级 MENU = 页面；API 节点不进菜单
function hasInvitePartnerMenu(menus) {
  return menus.some((item) => {
    if (item.path === '/invite-partner' || item.title === '邀请合伙人' || item.title === '合伙人管理') return true
    return (item.children || []).some(
      (child) =>
        child.path === '/invite-partner' ||
        child.title === '邀请合伙人' ||
        child.title === '合伙人管理'
    )
  })
}

function injectInvitePartnerMenu(menus, userInfo) {
  if (hasInvitePartnerMenu(menus)) return menus
  if (
    !isSuperAdmin(userInfo) &&
    !hasModulePermission(userInfo, 'partners') &&
    !hasModulePermission(userInfo, 'invite-partner')
  ) {
    return menus
  }
  const item = {
    id: 'invite-partner-fallback',
    title: '合伙人管理',
    path: '/invite-partner',
    icon: 'el-icon-share',
    index: '/invite-partner',
    permissionCode: 'partners:manage',
    children: []
  }
  const userIndex = menus.findIndex(
    (menu) =>
      menu.path === '/user' ||
      menu.title === '用户管理' ||
      (menu.children || []).some((child) => child.path === '/user')
  )
  if (userIndex >= 0) {
    menus.splice(userIndex + 1, 0, item)
  } else {
    menus.push(item)
  }
  return menus
}

export function buildPermissionMenus(permissions, userInfo) {
  const source = Array.isArray(permissions) ? permissions : []
  const menus = source.reduce((menus, parent) => {
    if (!parent || !isMenuPermission(parent)) return menus

    const childMenus = dedupeByPath(
      (parent.children || [])
        .filter((child) => isMenuPermission(child))
        .map((child) => {
          const path = resolvePath(child)
          return path ? toMenuItem(child, path) : null
        })
        .filter(Boolean)
    )

    if (childMenus.length) {
      menus.push({
        id: parent.id,
        title: parent.permissionName,
        icon: ICON_BY_PATH[resolvePath(parent)] || childMenus[0].icon,
        index: `menu-${parent.id || parent.permissionCode || parent.permissionName}`,
        permissionCode: parent.permissionCode,
        children: childMenus
      })
      return menus
    }

    const parentPath = resolvePath(parent)
    if (parentPath) {
      menus.push({
        ...toMenuItem(parent, parentPath),
        index: parentPath,
        children: []
      })
    }
    return menus
  }, [])

  const dashboardMenus = menus.filter(isDashboardMenu)
  const sourceMenus = menus.filter((item) => !isDashboardMenu(item))
  return injectInvitePartnerMenu([...dashboardMenus, ...sourceMenus], userInfo)
}

function isDashboardMenu(item) {
  if (!item) return false
  const code = String(item.permissionCode || '').trim()
  return (
    item.path === '/dashboard' ||
    item.title === '数据看板' ||
    item.title === '整体看板' ||
    code === 'dashboard' ||
    code === 'dashboard:overall'
  )
}
